<template>
    <div class="spotlight-root">
        <Header :label="fullName" overlap="small" />
        <UserNavSection />
        <MainLayout
            marginTop
            :class="{ disabled: disableScreen }"
            hideOnMobile="right"
        >
            <ProfileModule slot="right" dense />
            <FeaturedResource slot="right" />
            <VerifyModule slot="right" />
            <ProfileModule slot="mobTop" dense />
            <PersonalDetails
                id="personal"
                ref="personalDetails"
                slot="center"
                v-model="user"
                :isLoading="isLoadingData"
                @image:changed="
                    img => {
                        changedImage = img;
                    }
                "
                @image:removed="
                    () => {
                        changedImage = null;
                    }
                "
                @removeOldImage="
                    () => {
                        user.image = null;
                    }
                "
            />
            <ContactDetails
                v-show="true"
                id="contact"
                slot="center"
                v-model="user"
                :oldUserData="oldUserData()"
                :isLoading="isLoadingData"
            />

            <Languages
                id="language"
                slot="center"
                v-model="candidateData.languages"
                :oldLanguage="oldCandidateData().languages"
                :isLoading="isLoadingData"
            />
            <Education
                id="education"
                slot="center"
                v-model="candidateData.degrees"
                :oldDegrees="oldCandidateData().degrees"
                :isLoading="isLoadingData"
            />
            <WorkAuthorization
                slot="center"
                v-model="workAuthorizationData"
                :isLoading="isLoadingData"
            />
            <Communication
                slot="center"
                v-model="user.newsLetterConsent"
                :isLoading="isLoadingData"
            />
        </MainLayout>

        <transition name="slide-in-out">
            <div v-if="anyDataChanged" class="footer">
                <div style="height: 20px">
                    <LoadingBar
                        v-if="loadingBar"
                        class="loadingbar"
                        :speed="loadingSpeed"
                        :target="loadingBar"
                        animated
                    />
                </div>
                <div class="footer-info">
                    <div class="align-self-center" style="flex: 2;">
                        <h6
                            class="mb-0 pr-3"
                            style="text-align: right; line-height: 33px;"
                        >
                            {{ $t('spotlight.saveChangesToSubmit') }}
                        </h6>
                    </div>
                    <div style="flex: 1;">
                        <BaseButton
                            variant="secondary"
                            class="px-5 py-1"
                            @click="onSave"
                        >
                            <div
                                class="d-flex align-items-center justify-content-center"
                            >
                                <BaseIcon name="save" class="mr-1" size="lg" />
                                {{ $t('spotlight.saveChanges') }}
                            </div>
                        </BaseButton>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
// Project components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import UserNavSection from '~/shared/UserNavSection';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import FeaturedResource from '~/components/smart/FeaturedResource';
import LoadingBar from '~/shared/LoadingBar';
import VerifyModule from '~/components/miscellaneous/VerifyModule';

// Page Components
import PersonalDetails from './components/PersonalDetails';
import ContactDetails from './components/ContactDetails';
import Languages from './components/Languages';
import Education from './components/Education';
import Communication from './components/Communication';
import WorkAuthorization from './components/WorkAuthorization';

// Utils
import CandidateService from '~/services/CandidateService';
import IntercomService from '~/services/IntercomService';
import DialogService from '~/services/DialogService';
import MessageService from '~/services/MessageService';
import deepClone from '~/utils/deepClone';
import userDataToName from '~/utils/user/userDataToName';
import spotlightErrorMapper from '~/utils/errors/spotlightErrorMapper';
import { extractErrorResponse } from '~/utils/errors/authErrorMapper';
import { emailValidator } from '~/utils/validators';

export default {
    components: {
        Header,
        MainLayout,
        UserNavSection,
        ProfileModule,
        VerifyModule,
        PersonalDetails,
        FeaturedResource,
        ContactDetails,
        Languages,
        Education,
        Communication,
        LoadingBar,
        WorkAuthorization,
    },
    name: 'Spotlight',
    data() {
        return {
            forceCandidateUpdate: false,
            forceUserUpdate: false,
            user: {}, // The unsaved user data (firstName, nationality ... etc)
            candidateData: {}, // The unsaved candidate data (degrees, language ... etc )
            workAuthorizationData: [],
            oldWorkAuthorizationData: [],
            loadingBar: 0,
            loadingSpeed: 1,
            profileId: null,
            dataChanged: {
                candidate: false,
                user: false,
                workAuth: false,
                image: false,
            },
            changedImage: null, // The image to save,
        };
    },
    computed: {
        shouldCreateModal() {
            return this.$interfaces.unsavedChanges.isOpen();
        },
        candidate() {
            const temp = this.forceCandidateUpdate;
            this.forceCandidateUpdate = false;
            return this.$interfaces.candidate.me.get({
                reactive: true,
                forceUpdate: temp,
            });
        },
        userData() {
            const temp = this.forceUserUpdate;
            this.forceUserUpdate = false;
            return this.$interfaces.user.me.get({
                reactive: true,
                forceUpdate: temp,
            });
        },
        loading() {
            return this.candidate.loading;
        },
        anyDataChanged() {
            return (
                this.dataChanged.candidate ||
                this.dataChanged.user ||
                this.dataChanged.workAuth ||
                this.dataChanged.image
            );
        },
        userLoading() {
            return this.userData.loading;
        },
        disableScreen() {
            return this.loadingBar > 0 && this.loadingBar < 100;
        },
        isLoading() {
            return this.loading || this.userLoading;
        },
        isLoadingData() {
            // Returns true if is loading and no data has been fetched yet.
            // This is only for when the page is loading for the first time.
            return this.isLoading && (!this.candidateData.id || !this.user.id);
        },
        fullName() {
            let data = this.userData;
            data = data ? data.data : {};
            return userDataToName(data, 'spotlight.header');
        },
    },
    methods: {
        oldCandidateData() {
            return this.candidate.data || {};
        },
        oldUserData() {
            // this.userData.data is populated with an empty array before getting real data.
            if (
                Array.isArray(this.userData.data) &&
                this.userData.data.length == 0
            ) {
                return {};
            }

            return this.userData.data || {};
        },
        replaceCandidateData() {
            this.candidateData = deepClone(this.candidate.data) || {};
            if (this.candidateData instanceof Array) {
                this.candidateData = {};
            }
        },
        replaceUserData() {
            this.user = deepClone(this.userData.data);
            if (this.user instanceof Array) {
                this.user = {};
            }
        },
        hasDataChanged(newData, oldData) {
            const a = JSON.stringify(newData);
            const b = JSON.stringify(oldData);
            return a !== b;
        },
        createOnSaveMessage() {
            MessageService.newMessage(
                this.$t('general.success').concat('!'),
                this.$t('spotlight.saveSuccessful')
            );
        },
        async onSave() {
            this.loadingBar = 10;
            const candidateData = this.candidateData || {};
            const userData = this.user || {};
            const oldUserData = this.userData;

            const workAuthData = this.workAuthorizationData;
            const oldWorkAuthData = this.oldWorkAuthorizationData;

            const errors = [];

            // User data
            const newUserData = {
                firstName: userData.firstName,
                lastName: userData.lastName,
                nationality: userData.nationality,
                gender: userData.gender,
                phoneNumber: userData.phoneNumber,
                newsLetterConsent: userData.newsLetterConsent,
            };
            // If email is not set, validate email, and add it to the update request
            if (
                userData.email &&
                !oldUserData.email &&
                emailValidator(userData.email)
            ) {
                newUserData.email = userData.email;
            }
            if (userData.image === null) {
                newUserData.avatar = '';
            }

            // Save user data
            await CandidateService.saveUserData(newUserData).catch(
                responseError => {
                    errors.push(...(extractErrorResponse(responseError) || []));
                }
            );

            this.loadingBar = 30;

            // User image - if image was changed upload it
            if (this.changedImage) {
                await CandidateService.saveUserImage(this.changedImage).catch(
                    responseErrors => errors.push(...responseErrors)
                );
                this.changedImage = null;
                this.$refs.personalDetails.resetChangedImage();
            }

            // Save degrees
            const oldCandidateData = this.oldCandidateData();
            await CandidateService.saveDegrees(
                candidateData.degrees,
                oldCandidateData.degrees
            ).catch(responseErrors => {
                errors.push(...responseErrors);
            });

            this.loadingBar = 50;

            await CandidateService.saveWorkAuthorizations(
                workAuthData,
                oldWorkAuthData
            ).catch(responseErrors => {
                errors.push(...responseErrors);
            });

            this.loadingBar = 90;

            // Save languages
            await CandidateService.saveLanguages(
                candidateData.languages,
                oldCandidateData.languages
            ).catch(responseErrors => {
                errors.push(...responseErrors);
            });

            // Sets loading bar to 100%
            this.loadingBar = 100;
            this.loadingSpeed = 100;

            // Reloads the saved results
            this.forceCandidateUpdate = true;
            this.forceUserUpdate = true;
            await this.loadWorkAuthorization();

            // Creates a successful-save message
            if (errors.length > 0) {
                // Show error messages
                MessageService.errorWithMapper(
                    null,
                    errors[0],
                    spotlightErrorMapper,
                    spotlightErrorMapper
                );
            } else {
                // Show succcess message
                this.createOnSaveMessage();
            }

            IntercomService.setUserData();
            IntercomService.update();

            this.loadingBar = 0;
            this.loadingSpeed = 1;
        },
        async loadWorkAuthorization() {
            await this.$interfaces.candidate.workauthorization
                .get({
                    forceUpdate: true,
                })
                .then(data => {
                    this.oldWorkAuthorizationData = deepClone(data.data);
                    this.workAuthorizationData = data.data;
                });
        },
    },
    async mounted() {
        this.replaceUserData();
        const scrollTo = this.$route.query.scrollTo;
        if (scrollTo) {
            this.$scrollTo(scrollTo);
        }
        await this.$interfaces.user.me.get();
        await this.$interfaces.candidate.me.get().then(data => {
            this.candidateData = deepClone(data.data);
        });

        await this.loadWorkAuthorization();
    },
    watch: {
        loading(isLoading, wasLoading) {
            if (!isLoading && wasLoading) {
                this.replaceCandidateData();
            }
        },
        userLoading(isLoading, wasLoading) {
            if (!isLoading && wasLoading) {
                this.replaceUserData();
            }
        },
        candidateData: {
            deep: true,
            handler() {
                this.dataChanged.candidate = this.hasDataChanged(
                    this.candidateData,
                    this.oldCandidateData()
                );
            },
        },
        workAuthorizationData(newData) {
            this.dataChanged.workAuth = this.hasDataChanged(
                newData,
                this.oldWorkAuthorizationData
            );
        },
        user: {
            deep: true,
            handler() {
                this.dataChanged.user = this.hasDataChanged(
                    this.user,
                    this.oldUserData()
                );
            },
        },
        anyDataChanged(shouldShow, wasShown) {
            const newValue = shouldShow;
            if (newValue !== wasShown) {
                this.$interfaces.unsavedChanges.setHasUnsavedChanges(newValue);
            }
        },
        changedImage(val) {
            this.dataChanged.image = Boolean(val);
        },
        shouldCreateModal(shouldShow, wasShown) {
            if (shouldShow && !wasShown) {
                DialogService.openSpotlightExitDialog(
                    this.onSave,
                    this.$router
                );
            }
        },
    },
};
</script>

<style scoped>
.spotlight-root {
    padding-bottom: 100px;
}

.footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0px;
    height: 80px;
    background-color: white;
    transition: bottom 2s;
    box-shadow: 0 -2px 5px var(--light-gray);

    display: flex;
    flex-direction: column;
}
.footer .footer-info {
    display: flex;
}

@media only screen and (max-width: 600px) {
    .footer {
        background-color: transparent;
        box-shadow: none;
        justify-content: flex-end;
    }
    .footer-info > *:first-child {
        display: none;
    }
    button {
        width: 90%;
        margin: 0 5%;
        margin-bottom: 5px;
        text-align: center !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15), 0 2px 3px rgba(0, 0, 0, 0.2);
    }
}

.slide-in-out-enter-active,
.slide-in-out-leave-active {
    transition: bottom 0.7s;
}
.slide-in-out-enter,
.slide-in-out-leave-to {
    bottom: -100px;
}

.loadingbar {
    height: 6px;
}
</style>
